/**
 * LstItem
 *
 * Component representing a single row in the users list table.
 * Displays user info such as name, email, business name, roles, contact, and available actions.
 *
 * @param {Object} props
 * @param {string} props.id - The user ID.
 * @param {boolean} [props.isFirst] - Indicates if the item is the first in the list (used for targeting).
 * @returns {JSX.Element} Table row representing a user.
 */
import React, { useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import PropTypes from 'prop-types'
import { observer } from 'mobx-react-lite'
import { Button, Popconfirm, Popover, Select, Space, Tag } from 'antd'
import { InfoOutlined } from '@ant-design/icons'
import { DO_LOAD } from '../../../config'
import { useStores } from '../../../cls/stores/MobXProvider'
import { actLoadUser, actSaveUser } from '../../../stores/users/actions'
import {
	CustomTable,
	Filter,
	FormatedTimestamp,
	LstTable
} from '../../../components/Shared'
import QuickUserContact from '../../../components/Shared/QuickUserContact'
import {
	GeneralContentWrapper,
	GeneralTitle
} from '../../../components/Shared/GeneralComponents'
import {
	actLoadUsers,
	actUndoOrDeleteUser
} from '../../../stores/ui-lst-users/actions'
import { useSmartTranslation } from '../../../cls/TranslationContext'
import { stripeNewPromoCodeForUser } from '../../../stores/subscriptions/actions'
import { runInAction, toJS } from 'mobx'

/*
 * @LstItem
 * List user email
 */
const LstItem = observer(({ id, isFirst }) => {
	const { t } = useSmartTranslation()
	const navigate = useNavigate()
	const { users } = useStores()
	const user = users.requireUserById(id)
	// console.log('LstItem user', user.promoCodes)
	const { loggedInUser } = users
	const isAdmin = loggedInUser.isAdmin
	const isSuperAdmin = loggedInUser.isSuperAdmin

	const userAllowed = user?.allowed || {}

	useEffect(() => {
		// Fetch only if user is empty
		if (user.loaded === DO_LOAD) {
			actLoadUser(users, id, true)
				.catch(console.error)
				.finally(() => {
					users.saveState()
				})
		}
	}, []) // Depend only on userId

	// action options
	const _actions = [
		{
			key: 'view',
			label: t('single.view'),
			value: 'view'
		},
		{
			key: 'data',
			label: t('__edit%'),
			value: 'data'
		},
		{
			key: 'promo',
			label: t('single.create-promo-code'),
			value: 'promo'
		}
	]

	// allow to delete a user if current user is admin or super admin or has permission to delete
	if (isAdmin || isSuperAdmin || users.canUser('delete', 'users')) {
		if (user.to_delete) {
			_actions.push({
				key: 'undo-del',
				label: t('single.delete-undo'),
				value: 'undo-delete'
			})
		} else {
			_actions.push({
				key: 'delete',
				label: t('single.delete'),
				value: 'delete'
			})
		}
	}

	/**
	 * handleActions
	 *
	 * Executes the appropriate navigation or state changes based on the selected user action.
	 *
	 * @param {string} value - The selected action key ('view', 'edit', 'delete', or 'undo-delete').
	 */
	const handleActions = (value) => {
		switch (value) {
			case 'view':
				navigate(`${id}/view`)
				break
			case 'data':
				navigate(`${id}/edit`)
				break
			case 'delete':
				setConfirmUndoOrDelete(true)
				break
			case 'undo-delete':
				setConfirmUndoOrDelete(true)
				break
			case 'promo':
				stripeNewPromoCodeForUser(user).then((res) => {
					if (res.status === 'success') {
						runInAction(() => {
							user.setPromoCodes(res.codes)
							actSaveUser(
								user.user_id,
								toJS(user.full),
								toJS(user.allowed)
							).then(() => {
								users.saveState()
							})
						})
					}
				})
			default:
				break
		}
	}

	/**
	 * confirmUndoOrDelete
	 *
	 * Confirms and executes the deletion or undo-delete action for a user.
	 */
	// State to manage the visibility of delete Popconfirm
	const [showConfirmUndoOrDelete, setConfirmUndoOrDelete] =
		React.useState(false)
	const confirmUndoOrDelete = () => {
		setConfirmUndoOrDelete(false)
		if (!!user.to_delete) {
			actUndoOrDeleteUser(id, false).catch(console.error)
		} else {
			actUndoOrDeleteUser(id, true).catch(console.error)
		}
	}

	return (
		<tr>
			<td>
				<Link to={`${id}/view`}>{user.name}</Link>
				{user.getActivePromoCode && (
					<Tag>{user.getActivePromoCode}</Tag>
				)}
			</td>
			<td> {user.country} </td>
			<td>
				<span>{user.email}</span>
			</td>
			<td>
				<Tag id={isFirst ? 'target-itm-account' : ''} color="default">
					{user.businessName}
				</Tag>
			</td>
			<td>
				{userAllowed && (
					<span>
						{[
							userAllowed?.admin && t('single.admin'),
							userAllowed?.superAdmin && t('single.super-admin'),
							userAllowed?.roles?.client && t('_client'),
							userAllowed?.roles?.clientAdmin &&
								t('_client.admin'),
							userAllowed?.roles?.team && t('single.team.label'),
							userAllowed?.roles?.teamAdmin &&
								t('single.team-admin')
						]
							.filter(Boolean) // Filter out any false values
							.join(', ')}{' '}
						{/* Join the roles with a comma */}
					</span>
				)}
			</td>
			<td>
				<QuickUserContact id={id} />
			</td>
			<td>
				<Space style={{ width: '100%' }}>
					<Popover
						content={() => (
							<CustomTable
								data={[
									[
										{
											item: (
												<strong>
													{t('single.info')}
												</strong>
											)
										}
									],
									[
										t('_client.multiple'),
										{
											item: <span>{user.clientsNr}</span>
										}
									],
									[
										t('single.team.label'),
										{
											item: <span>{user.teamNr}</span>
										}
									],
									[
										t('single.modified'),
										{
											item: (
												<FormatedTimestamp
													timestamp={user.modifiedTs}
													formatedAs="DD.MM.YYYY hh:mm"
												/>
											)
										}
									],
									[
										t('single.modified-related'),
										{
											item: (
												<FormatedTimestamp
													timestamp={
														user.modifiedRelatedTs
													}
													formatedAs="DD.MM.YYYY hh:mm"
												/>
											)
										}
									],
									[
										t('single.discount'),
										{
											item: (
												<span>
													{user.discount
														? user.discount
														: t('single.none')}
												</span>
											)
										}
									],
									[
										t('single.seller-code'),
										{
											item: (
												<span>
													{
														// this array needs to join as ,
														user.getActivePromoCodesString ||
															t('single.none')
													}
												</span>
											)
										}
									]
								]}
							/>
						)}
						trigger="hover"
					>
						<Button
							id={isFirst ? 'target-itm-info' : ''}
							color="default"
							icon={<InfoOutlined />}
						/>
					</Popover>

					<Space
						style={{ width: '100%' }}
						id={isFirst ? 'target-itm-actions' : ''}
					>
						<Select
							value={t('single.actions')}
							options={_actions}
							onChange={handleActions}
							style={{ width: 120 }}
						/>
						<Popconfirm
							title={
								user.to_delete
									? t('single.confirm-undo-delete')
									: t('single.confirm-delete')
							}
							open={showConfirmUndoOrDelete}
							onConfirm={confirmUndoOrDelete}
							onCancel={() => setConfirmUndoOrDelete(false)}
							okType="link"
							placement="topLeft"
						/>
					</Space>
				</Space>
				<div>
					{user.toDeleteTs && (
						<span>
							{t('single.delete-on')}
							<FormatedTimestamp timestamp={user.toDeleteTs} />
						</span>
					)}
				</div>
			</td>
		</tr>
	)
})

LstItem.propTypes = {
	id: PropTypes.string.isRequired
}

const UsersLst = observer(() => {
	const { t } = useSmartTranslation()
	const { uiLstUsers, appType } = useStores()
	const { appIs } = appType
	const byShowAll = uiLstUsers.filter.show_all
	const bySearch = uiLstUsers.search
	const byDel = uiLstUsers.del

	useEffect(() => {
		actLoadUsers().catch(console.error)
	}, [appIs, byShowAll, bySearch, byDel])

	return (
		<GeneralContentWrapper>
			<GeneralTitle text={t('_user.multiple')} />
			<Filter
				uiStore={uiLstUsers}
				isForUser={true}
				hide={[
					'progress',
					'priority',
					'category',
					'addNew',
					'by-user',
					'for-user',
					'orderBy',
					'entity_table',
					'kind'
				]}
			/>
			<LstTable
				className="table-pro"
				LstItem={LstItem}
				store={uiLstUsers}
			>
				<tr>
					<th>{t('single.name')}</th>
					<th>{t('single.country')}</th>
					<th>{t('single.email')}</th>
					<th>{t('single.account-name')}</th>
					<th>{t('_role.multiple')}</th>
					<th>{t('single.contact')}</th>
					<th>{t('_action.multiple')}</th>
				</tr>
			</LstTable>
		</GeneralContentWrapper>
	)
})

export default UsersLst
